import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Target, TrendingUp, ArrowLeft } from 'lucide-react';
import { apiService } from '../services/api';
import { Loading, ErrorMessage, Card } from '../components/SharedComponents';

function PlayerDetails() {
  const { playerId } = useParams();
  const [playerStats, setPlayerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFormat, setActiveFormat] = useState('t20');

  useEffect(() => {
    if (playerId) {
      fetchPlayerStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const fetchPlayerStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPlayerCareerStats(playerId);
      setPlayerStats(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading message="Loading player details..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchPlayerStats} />;
  if (!playerStats) return <ErrorMessage message="Player not found" />;

  const player = playerStats.player || {};
  const battingStats = playerStats.batting || {};
  const bowlingStats = playerStats.bowling || {};

  const formatStat = (value) => {
    if (value === null || value === undefined || value === '' || value === 0) return '-';
    if (typeof value === 'number') return value.toLocaleString();
    return value;
  };

  // Format map for better display
  const formatLabels = {
    't20': 'T20',
    't20i': 'T20 International',
    'odi': 'One Day International',
    'test': 'Test',
    'lista': 'List A',
    'firstclass': 'First Class',
    't10': 'T10'
  };

  // Get available formats
  const availableFormats = Object.keys(battingStats).filter(format =>
    battingStats[format] && typeof battingStats[format] === 'object'
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div>
        <Link
          to="/batting-stats"
          style={{
            color: '#007AFF',
            textDecoration: 'none',
            marginBottom: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '17px',
            fontWeight: '500',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <ArrowLeft size={20} />
          Back to Statistics
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginTop: '16px' }}>
          {player.logo_url || player.thumb_url ? (
            <img
              src={player.logo_url || player.thumb_url}
              alt={player.title}
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '4px solid #f5f5f7'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div style={{
            width: '96px',
            height: '96px',
            background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
            borderRadius: '50%',
            display: player.logo_url || player.thumb_url ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '4px solid #f5f5f7'
          }}>
            <User style={{ width: '48px', height: '48px', color: 'white' }} />
          </div>

          <div>
            <h1 style={{
              fontSize: '34px',
              fontWeight: '700',
              color: '#1d1d1f',
              margin: 0,
              letterSpacing: '-0.5px'
            }}>
              {player.title || player.name || 'Unknown Player'}
            </h1>
            <p style={{
              color: '#86868b',
              marginTop: '8px',
              fontSize: '17px',
              fontWeight: '500',
              margin: '8px 0 0 0'
            }}>
              {player.playing_role ? player.playing_role.charAt(0).toUpperCase() + player.playing_role.slice(1) : ''} • {player.nationality || player.country?.toUpperCase() || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Player Info Card */}
      <Card>
        <h2 style={{
          fontSize: '22px',
          fontWeight: '600',
          marginBottom: '20px',
          color: '#1d1d1f'
        }}>
          Player Information
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px'
        }}>
          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Full Name
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px' }}>
              {player.first_name || player.title || 'N/A'}
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Date of Birth
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px' }}>
              {player.birthdate ? new Date(player.birthdate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Birthplace
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px' }}>
              {player.birthplace || 'N/A'}
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Batting Style
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px' }}>
              {player.batting_style || player.battingStyle || 'N/A'}
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Bowling Style
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px' }}>
              {player.bowling_style || player.bowlingStyle || 'N/A'}
            </p>
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '10px'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Playing Role
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px', textTransform: 'capitalize' }}>
              {player.playing_role || 'N/A'}
            </p>
          </div>
        </div>
      </Card>

      {/* Format Selector */}
      {availableFormats.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          padding: '4px',
          borderBottom: '1px solid #e5e5e7'
        }}>
          {availableFormats.map((format) => (
            <button
              key={format}
              onClick={() => setActiveFormat(format)}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderBottom: activeFormat === format ? '3px solid #007AFF' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: activeFormat === format ? '#007AFF' : '#86868b',
                fontWeight: activeFormat === format ? '600' : '500',
                cursor: 'pointer',
                fontSize: '15px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (activeFormat !== format) {
                  e.currentTarget.style.color = '#1d1d1f';
                }
              }}
              onMouseLeave={(e) => {
                if (activeFormat !== format) {
                  e.currentTarget.style.color = '#86868b';
                }
              }}
            >
              {formatLabels[format] || format.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* Batting Statistics */}
      {battingStats[activeFormat] && Object.keys(battingStats[activeFormat]).length > 0 && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <TrendingUp size={24} color="#007AFF" />
            <h2 style={{ fontSize: '22px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>
              Batting Statistics - {formatLabels[activeFormat] || activeFormat.toUpperCase()}
            </h2>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f7' }}>
                  <th style={tableHeaderStyle}>Matches</th>
                  <th style={tableHeaderStyle}>Innings</th>
                  <th style={tableHeaderStyle}>Not Out</th>
                  <th style={tableHeaderStyle}>Runs</th>
                  <th style={tableHeaderStyle}>Balls</th>
                  <th style={tableHeaderStyle}>Highest</th>
                  <th style={tableHeaderStyle}>Average</th>
                  <th style={tableHeaderStyle}>Strike Rate</th>
                  <th style={tableHeaderStyle}>100s</th>
                  <th style={tableHeaderStyle}>50s</th>
                  <th style={tableHeaderStyle}>4s</th>
                  <th style={tableHeaderStyle}>6s</th>
                  <th style={tableHeaderStyle}>Catches</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e5e5e7' }}>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].matches)}</td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].innings)}</td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].notout)}</td>
                  <td style={{ ...tableCellStyle, fontWeight: '700', color: '#007AFF' }}>
                    {formatStat(battingStats[activeFormat].runs)}
                  </td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].balls)}</td>
                  <td style={{ ...tableCellStyle, fontWeight: '600', color: '#FF9500' }}>
                    {formatStat(battingStats[activeFormat].highest)}
                  </td>
                  <td style={{ ...tableCellStyle, fontWeight: '600' }}>
                    {formatStat(battingStats[activeFormat].average)}
                  </td>
                  <td style={{ ...tableCellStyle, fontWeight: '600' }}>
                    {formatStat(battingStats[activeFormat].strike)}
                  </td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].run100)}</td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].run50)}</td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].run4)}</td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].run6)}</td>
                  <td style={tableCellStyle}>{formatStat(battingStats[activeFormat].catches)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Bowling Statistics */}
      {bowlingStats[activeFormat] && Object.keys(bowlingStats[activeFormat]).length > 0 && parseInt(bowlingStats[activeFormat].wickets || 0) > 0 && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Target size={24} color="#34C759" />
            <h2 style={{ fontSize: '22px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>
              Bowling Statistics - {formatLabels[activeFormat] || activeFormat.toUpperCase()}
            </h2>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f7' }}>
                  <th style={tableHeaderStyle}>Matches</th>
                  <th style={tableHeaderStyle}>Innings</th>
                  <th style={tableHeaderStyle}>Balls</th>
                  <th style={tableHeaderStyle}>Overs</th>
                  <th style={tableHeaderStyle}>Runs</th>
                  <th style={tableHeaderStyle}>Wickets</th>
                  <th style={tableHeaderStyle}>Best</th>
                  <th style={tableHeaderStyle}>Average</th>
                  <th style={tableHeaderStyle}>Economy</th>
                  <th style={tableHeaderStyle}>Strike Rate</th>
                  <th style={tableHeaderStyle}>5W</th>
                  <th style={tableHeaderStyle}>4W</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e5e5e7' }}>
                  <td style={tableCellStyle}>{formatStat(bowlingStats[activeFormat].matches)}</td>
                  <td style={tableCellStyle}>{formatStat(bowlingStats[activeFormat].innings)}</td>
                  <td style={tableCellStyle}>{formatStat(bowlingStats[activeFormat].balls)}</td>
                  <td style={tableCellStyle}>{formatStat(bowlingStats[activeFormat].overs)}</td>
                  <td style={tableCellStyle}>{formatStat(bowlingStats[activeFormat].runs)}</td>
                  <td style={{ ...tableCellStyle, fontWeight: '700', color: '#34C759' }}>
                    {formatStat(bowlingStats[activeFormat].wickets)}
                  </td>
                  <td style={{ ...tableCellStyle, fontWeight: '600', color: '#FF9500' }}>
                    {formatStat(bowlingStats[activeFormat].bestinning)}
                  </td>
                  <td style={{ ...tableCellStyle, fontWeight: '600' }}>
                    {formatStat(bowlingStats[activeFormat].average)}
                  </td>
                  <td style={{ ...tableCellStyle, fontWeight: '600' }}>
                    {formatStat(bowlingStats[activeFormat].econ)}
                  </td>
                  <td style={{ ...tableCellStyle, fontWeight: '600' }}>
                    {formatStat(bowlingStats[activeFormat].strike)}
                  </td>
                  <td style={tableCellStyle}>{formatStat(bowlingStats[activeFormat].wicket5i)}</td>
                  <td style={tableCellStyle}>{formatStat(bowlingStats[activeFormat].wicket4i)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* No stats available */}
      {(!battingStats[activeFormat] || Object.keys(battingStats[activeFormat]).length === 0) &&
       (!bowlingStats[activeFormat] || Object.keys(bowlingStats[activeFormat]).length === 0) && (
        <Card>
          <p style={{
            color: '#86868b',
            textAlign: 'center',
            padding: '48px',
            fontSize: '15px'
          }}>
            No career statistics available for {formatLabels[activeFormat] || activeFormat.toUpperCase()} format
          </p>
        </Card>
      )}

      {/* Social Media Links */}
      {(player.facebook_profile || player.twitter_profile || player.instagram_profile) && (
        <Card>
          <h3 style={{
            fontSize: '17px',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#1d1d1f'
          }}>
            Social Media
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {player.facebook_profile && (
              <a
                href={player.facebook_profile}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#1877F2',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                Facebook
              </a>
            )}
            {player.twitter_profile && (
              <a
                href={player.twitter_profile}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#1DA1F2',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                Twitter
              </a>
            )}
            {player.instagram_profile && (
              <a
                href={player.instagram_profile}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                Instagram
              </a>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// Reusable table styles
const tableHeaderStyle = {
  padding: '12px 16px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '13px',
  color: '#1d1d1f',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tableCellStyle = {
  padding: '14px 16px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#1d1d1f'
};

export default PlayerDetails;
